import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { venueQueries } from "../features/venues/api/queries";
import { areaQueries } from "../features/weather/api/queries";
import { useOffset } from "../shared/lib/paging";
import { AppHeader } from "../shared/ui/AppHeader";
import { Pager } from "../shared/ui/Pager";

/** How many venues a page holds. Each row carries images and links. */
const PAGE_SIZE = 20;

export const Route = createFileRoute("/_authenticated/venues/")({
  loader: ({ context: { queryClient } }) =>
    /*
     * Areas alongside the list, even though this page no longer renders a form:
     * the add and edit dialogs open over it, and preloading on intent means the
     * modal has its picker options ready the moment it appears.
     */
    Promise.all([
      queryClient.ensureQueryData(venueQueries.page({ limit: PAGE_SIZE })),
      queryClient.ensureQueryData(areaQueries.list()),
    ]),
  component: VenuesPage,
  errorComponent: ({ error }) => (
    <main className="p-6">
      <p role="alert" className="text-brand">
        Couldn’t load venues: {error.message}
      </p>
    </main>
  ),
});

/**
 * The venue list. Adding and editing happen in a dialog over it, at
 * `/venues/new` and `/venues/$venueId/edit`.
 *
 * Seeing the list while adding to it is what stops someone entering a
 * duplicate, which is why the form opens as a modal over this page rather than
 * replacing it.
 *
 * No delete, deliberately: `events.venueId` is a real FK, so the server exposes
 * create and update only.
 */
function VenuesPage() {
  const page = useOffset(PAGE_SIZE);
  const { data } = useSuspenseQuery(
    venueQueries.page({ limit: PAGE_SIZE, offset: page.offset }),
  );

  const venues = data.venuePage.items;
  const total = data.venuePage.totalCount;
  page.reconcile(total);

  return (
    <>
      <AppHeader />
      <main className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Venues</h1>
            <p className="mt-1 text-sm text-content-muted">
              Shared across everyone’s events. Correct one if it’s wrong — they
              aren’t owned by whoever added them.
            </p>
          </div>
          <Link
            to="/venues/new"
            className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
          >
            Add venue
          </Link>
        </div>

        <div className="mt-8">
          {venues.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-subtle p-10 text-center">
              <p className="font-medium">No venues yet</p>
              <p className="mt-1 text-sm text-content-muted">
                Add the first one to attach events to it.
              </p>
              <Link
                to="/venues/new"
                className="mt-4 inline-block rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
              >
                Add venue
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {venues.map((venue) => (
                <li
                  key={venue.id}
                  className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle bg-surface p-3"
                >
                  {/*
                    Primary first, falling back to whatever is first — the server
                    orders the list, so [0] is the deliberate choice when nothing
                    is marked primary.
                  */}
                  {(() => {
                    const cover =
                      venue.images.find((image) => image.isPrimary) ??
                      venue.images[0];
                    return cover ? (
                      <img
                        src={cover.thumbnailUrl}
                        alt={cover.altText ?? ""}
                        className="h-14 w-14 shrink-0 rounded object-cover"
                      />
                    ) : null;
                  })()}

                  <div className="mr-auto">
                    <p className="font-medium">
                      {/*
                        The row still edits in a dialog; the name opens the
                        detail page, which is where the rooms and everything
                        attached to the venue live.
                      */}
                      <Link
                        to="/venues/$venueId"
                        params={{ venueId: venue.id }}
                        className="hover:underline"
                      >
                        {venue.name}
                      </Link>
                      {venue.isOutdoor && (
                        <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-content-muted">
                          Outdoor
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-content-muted">
                      {[venue.neighbourhood, venue.addressLine]
                        .filter(Boolean)
                        .join(" · ") || "No address"}
                    </p>
                    {/* Called out when absent, since that silently means no
                        forecast — the one case worth explaining. */}
                    <p className="text-xs text-content-muted">
                      {venue.area
                        ? `Weather: ${venue.area.name}`
                        : "No weather area set"}
                    </p>
                    {/* Only when there are any. Most venues have none, and an
                        empty "Rooms:" on every row would be noise. */}
                    {venue.rooms.length > 0 && (
                      <p className="text-xs text-content-muted">
                        Rooms: {venue.rooms.join(", ")}
                      </p>
                    )}
                  </div>
                  <Link
                    to="/venues/$venueId/edit"
                    params={{ venueId: venue.id }}
                    className="shrink-0 rounded-md border border-border-subtle px-3 py-1 text-sm hover:bg-surface-muted"
                  >
                    Edit
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <Pager
            offset={page.offset}
            limit={PAGE_SIZE}
            totalCount={total}
            onOffsetChange={page.setOffset}
            noun="venues"
          />
        </div>
      </main>
    </>
  );
}
